import type { EnrollmentWithSubject } from '@repo/shared-types';
import type { ExistingEnrollmentInfo } from './confirmRows';

/**
 * Builds studentId -> "their live enrollment in the class's subject" from
 * `useEnrollmentsForSubject(subjectId, null)` (every status, not just
 * ACTIVE). ACTIVE wins over PAUSED when somehow both exist; ENDED is
 * ignored entirely (spec: a new enrollment is created fresh for it, exactly
 * like a student with no enrollment at all). Used by CreateTab and PickTab
 * to prefill `DraftMemberRow.existingEnrollment` — the ConfirmTable then
 * derives KEEP/REPRICE/PAUSED-exclusion from that one field, never
 * re-deriving it from the raw enrollment list itself.
 */
export function buildExistingEnrollmentMap(
  enrollments: EnrollmentWithSubject[],
): Map<string, ExistingEnrollmentInfo> {
  const map = new Map<string, ExistingEnrollmentInfo>();
  for (const e of enrollments) {
    if (e.status !== 'ACTIVE' && e.status !== 'PAUSED') continue;
    const already = map.get(e.student.id);
    if (already && !already.paused) continue; // an ACTIVE row already won this student
    map.set(e.student.id, {
      enrollmentId: e.id,
      pricingType: e.pricingType,
      rate: e.pricingType === 'MONTHLY_FLAT' ? (e.monthlyRate ?? 0) : (e.ratePerSession ?? 0),
      paused: e.status === 'PAUSED',
    });
  }
  return map;
}
